/** @jsx jsx */
import { css, jsx, SerializedStyles } from "@emotion/core"
import React from "react"
import classnames from "classnames"
import { Fields } from "@cd2/forms"
import { QuickToolTip } from "../../interfaceParts/QuickTooltip"
import { KeyValueList } from "../../interfaceParts/KeyValueList"
import { formatFullDateTime } from "../../../utils/date"
import { DropdownMenu } from "../../interfaceParts/DropdownMenu"
import { IconButton } from "@cd2/components"
import { IComponentProp } from "@cd2/tools"

type TableColumn<T> = {
  heading?: React.ReactNode
  hidden?: boolean
  narrow?: boolean
  noPadding?: boolean
  tdCss?: (row: T) => SerializedStyles
  render(row: T, i: number): React.ReactNode
  sortKey?: string
}

type TableOpts<T> = {
  data: T[]
  columns: (false | TableColumn<T>)[]
  rowClass?: (row: T, i: number) => string | undefined
  keyExtractor?: (row: T) => string
  css?: SerializedStyles
  additionalRow?: {
    show: (row: T) => boolean
    singleColumn: true
    css?: SerializedStyles
    render(row: T, i: number): React.ReactNode
  }
  sort?: { by: string; direction: "ASC" | "DESC" | "asc" | "desc" } | null
  onSort?: (value: { by: string; direction: "ASC" | "DESC" }) => void
}
export function renderTable<T>(opts: TableOpts<T>) {
  const columns = opts.columns.filter(Boolean).filter(col => !col.hidden)
  return (
    <table
      css={css(
        css`
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          border: 1px solid #dddfe3;
          border-radius: 8px;

          thead {
            background-color: white;
          }

          th {
            padding: 16px 12px;
            font-weight: 600;
            font-size: 12px;
            color: #444;
            white-space: nowrap;
            background: #fafafb;
            border-radius: 8px 8px 0 0;

            &.sortable {
              cursor: pointer;
            }
          }

          tbody {
            tr {
              border: 1px solid #dddfe3;
              border-left: none;
              border-right: none;
              transition: background-color 0.3s;
            }

            td {
              padding: 6px 12px;
              font-size: 13px;
              height: 48px;
            }
          }

          td.narrow,
          th.narrow {
            width: 0;
          }

          th.noPadding,
          td.noPadding {
            padding: 0 !important;
          }
        `,
        opts.css,
      )}
    >
      <thead>
        <tr>
          {columns.map((col, i) => (
            <th
              key={i}
              className={classnames({ narrow: col.narrow, sortable: opts.onSort && col.sortKey })}
              onClick={() => {
                if (opts.onSort && col.sortKey) {
                  opts.onSort({
                    by: col.sortKey,
                    direction:
                      opts.sort?.by === col.sortKey
                        ? opts.sort?.direction === "ASC"
                          ? "DESC"
                          : "ASC"
                        : "ASC",
                  })
                }
              }}
            >
              <div
                css={css`
                  display: flex;
                  gap: 8px;
                  align-items: center;
                `}
              >
                {col.heading}
                {opts.onSort && col.sortKey ? (
                  <AnimatedChevron
                    dir={
                      opts.sort?.by === col.sortKey
                        ? opts.sort.direction.toLowerCase() === "asc"
                          ? 1
                          : -1
                        : 0
                    }
                    size={16}
                  />
                ) : null}
              </div>{" "}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {opts.data.map((row, i) => (
          <React.Fragment key={opts.keyExtractor ? opts.keyExtractor(row) : i}>
            <tr className={opts.rowClass?.(row, i)}>
              {columns.map((col, j) => (
                <td
                  key={j}
                  className={classnames({ narrow: col.narrow, noPadding: col.noPadding })}
                  css={col.tdCss?.(row)}
                >
                  {col.render(row, i)}
                </td>
              ))}
            </tr>
            {opts.additionalRow?.show(row) && (
              <tr css={opts.additionalRow.css}>
                <td colSpan={columns.length}>
                  {opts.additionalRow.singleColumn ? opts.additionalRow.render(row, i) : null}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  )
}

export function renderTableCheckboxColumn(
  recordIds: string[],
  selectedIds: string[],
  onChange: (ids: string[]) => void,
  opts?: { disabledIds?: string[] },
): TableColumn<{ id: string }> {
  const disabledIds = new Set(opts?.disabledIds ?? [])
  const selectableIds = recordIds.filter(id => !disabledIds.has(id))
  const noneChecked = selectedIds.length === 0
  const allChecked =
    selectableIds.length > 0 && selectableIds.filter(id => !selectedIds.includes(id)).length === 0
  const partialChecked = !noneChecked && !allChecked

  return {
    heading: (
      <Fields.Checkbox
        indeterminate={partialChecked}
        value={allChecked}
        disabled={selectableIds.length === 0}
        onChange={() => {
          if (partialChecked || noneChecked) {
            onChange(selectableIds)
          } else {
            onChange([])
          }
        }}
      />
    ),
    narrow: true,
    render: row => {
      const disabled = disabledIds.has(row.id)
      return (
        <Fields.Checkbox
          value={selectedIds.includes(row.id)}
          disabled={disabled}
          onChange={value => {
            if (disabled) return
            onChange(value ? [...selectedIds, row.id] : selectedIds.filter(id => id !== row.id))
          }}
        />
      )
    },
  }
}

export function renderTableLastEditColumn(): TableColumn<{
  createdAt?: string | Date | null
  updatedAt?: string | Date | null
}> {
  return {
    heading: "Last Edit",
    sortKey: "lastEdit",
    render: row => (
      <QuickToolTip
        trigger="click"
        content={
          <KeyValueList
            data={[
              {
                label: "Created",
                value: row.createdAt ? formatFullDateTime(new Date(row.createdAt)) : "-",
              },
              {
                label: "Updated",
                value: row.updatedAt ? formatFullDateTime(new Date(row.updatedAt)) : "-",
              },
            ]}
          />
        }
        isLight
        showBorder
        interactive
      >
        <div
          css={css`
            white-space: nowrap;
          `}
        >
          {row.updatedAt && row.createdAt ? formatFullDateTime(new Date(row.updatedAt)) : "-"}
        </div>
      </QuickToolTip>
    ),
  }
}

export function renderTableMenuColumn<T>(
  items: (row: T) => IComponentProp<typeof DropdownMenu, "items">,
): TableColumn<T> {
  return {
    heading: "",
    narrow: true,
    noPadding: true,
    render: row => (
      <DropdownMenu
        target={() => (
          <div
            css={css`
              padding: 6px;
            `}
          >
            <IconButton
              icon="ellipsis-h"
              buttonStyle="outline"
              padding={4}
              buttonColor="#e7e7e7"
              color="#444"
              square
            />
          </div>
        )}
        items={items(row)}
      />
    ),
  }
}

function AnimatedChevron({
  dir,
  onClick,
  size = 32,
}: {
  dir: -1 | 0 | 1
  onClick?: () => void
  size?: number
}) {
  return (
    <div
      onClick={onClick}
      css={css`
        width: ${size}px;
        height: ${size}px;
        display: inline-block;
        vertical-align: middle;
      `}
    >
      <svg
        width={"100%"}
        height={"100%"}
        viewBox="0 0 100 100"
        css={css`
          ${!!onClick && "cursor: pointer;"}
          * {
            transition: 0.3s;
          }

          line {
            stroke: ${dir === 0 ? "#ccc" : "#333"};
            stroke-width: 10;
            stroke-linecap: round;
            transform-origin: 50px 50px;
          }

          ${dir === -1 &&
          css`
            g {
              transform: translate(0, 15%);
            }

            .line1 {
              transform: rotate(40deg);
            }

            .line2 {
              transform: rotate(-40deg);
            }
          `}
          ${dir === 1 &&
          css`
            g {
              transform: translate(0, -15%);
            }

            .line1 {
              transform: rotate(-40deg);
            }

            .line2 {
              transform: rotate(40deg);
            }
          `}
        `}
      >
        <g>
          <line className="line1" x1="10" y1="50" x2="50" y2="50" />
          <line className="line2" x1="90" y1="50" x2="50" y2="50" />
        </g>
      </svg>
    </div>
  )
}
